/**
 * Domain utility functions for working with platform and custom domains.
 */

const ACCEPTED_PLATFORM_SUFFIXES = [".launch10.site", ".launch10-dev.site"] as const;
const PLATFORM_SUFFIX = `.${import.meta.env.VITE_PLATFORM_DOMAIN ?? "launch10.site"}`;

/**
 * Check if a domain is a platform subdomain under any accepted zone
 * (prod "launch10.site" or dev "launch10-dev.site").
 */
export function isPlatformDomain(domain: string): boolean {
  return ACCEPTED_PLATFORM_SUFFIXES.some((s) => domain.endsWith(s));
}

/**
 * Extract subdomain from a full domain name.
 * For platform domains: "mysite.launch10.site" → "mysite"
 * For custom domains: "mybusiness.com" → "mybusiness"
 */
export function getSubdomain(domain: string): string {
  const match = ACCEPTED_PLATFORM_SUFFIXES.find((s) => domain.endsWith(s));
  if (match) {
    return domain.slice(0, -match.length);
  }
  return domain.split(".")[0];
}

/**
 * Build the full URL from domain and path.
 * Normalizes path "/" to empty string for cleaner URLs.
 */
export function getFullUrl(domain: string, path: string): string {
  const normalizedPath = path === "/" ? "" : path;
  return `${domain}${normalizedPath}`;
}

/**
 * Build a platform domain from a subdomain.
 * "mysite" → "mysite.launch10.site"
 */
export function buildPlatformDomain(subdomain: string): string {
  return `${subdomain}${PLATFORM_SUFFIX}`;
}

export { PLATFORM_SUFFIX };
